'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { usePathname } from '@/i18n/routing';
import { SiteSettings } from '@/types/site-settings';

import { Separator } from './ui/separator';
import { FooterAddress } from './footer-address';
import { SocialButtons } from './social-buttons';
import { SubscribeNewsletter } from './subscribe-newsletter';
import { UsefulLinks } from './useful-links';

const links = [
    {
        title: 'home',
        href: '/',
    },
    {
        title: 'about',
        href: '/about',
    },
    {
        title: 'privacy-policy',
        href: '/privacy-policy',
    },
    {
        title: 'recipes',
        href: '/recipes',
    },
    {
        title: 'terms-conditions',
        href: '/terms-and-conditions',
    },
    {
        title: 'cookies-policy',
        href: '/cookies-policy',
    },
];

export const Footer = ({ settings }: { settings: SiteSettings }) => {
    const pathname = usePathname();
    const t = useTranslations();

    if (pathname.includes('not-found')) {
        return null;
    }

    return (
        <footer className='bg-purple-light flex w-full flex-col text-white'>
            <div className='mx-auto flex w-full max-w-[1200px] flex-col items-start justify-between gap-y-6 px-4 pb-9 pt-12 lg:flex-row xl:px-0'>
                <SubscribeNewsletter />
                <div className='flex w-full flex-col items-start gap-y-6 lg:w-auto lg:flex-row lg:gap-[75px]'>
                    <UsefulLinks />
                    <div className='grid w-full grid-cols-2 gap-4 gap-x-[75px] lg:w-auto lg:gap-y-6'>
                        {links.map((link, index) => (
                            <Link
                                key={link.title}
                                href={link.href}
                                className={`text-[16px] font-[800] leading-[100%] tracking-normal ${index % 2 === 1 ? 'text-right lg:text-left' : ''}`}
                            >
                                {(text => (text ? text.charAt(0).toUpperCase() + text.slice(1) : ''))(t(link.title))}
                            </Link>
                        ))}
                    </div>
                </div>
                <div className='flex w-full flex-col items-center justify-center gap-0 lg:w-auto lg:gap-4'>
                    <SocialButtons />
                    <FooterAddress address={settings.address} />
                </div>
            </div>
            <Separator className='mx-auto hidden max-w-[1200px] opacity-10 lg:block' />
            <div className='mx-auto flex w-full max-w-[1200px] flex-col items-center justify-between gap-4 pb-4 lg:flex-row lg:pt-6'>
                <p className='text-sm font-normal leading-6 opacity-80'>{t('copyright')}</p>
                <Separator className='mx-auto max-w-[1200px] opacity-10 md:hidden' />

                <p className='text-sm font-normal leading-6 opacity-80'>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t('created-by').replace(
                                'MegaSec',
                                `<a href="https://megasec.az" target="_blank" rel="noopener noreferrer" class="hover:underline">MegaSec</a>`,
                            ),
                        }}
                    />
                </p>
            </div>
        </footer>
    );
};
